import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { environment } from '../../../../../../environments/environment';
import {
  ApprovalFilters,
  ApprovalOrderBy,
  ApprovalOrderDirection,
  GetApprovalRequestsResponse,
  GetMyRequestsResponse,
  GetRequestersResponse,
  ApproveRequestResult,
  RejectRequestResult,
  ConfirmApproveRequestBody,
  ConfirmRejectRequestBody,
  CancelApprovalRequestBody,
  CancelApprovalRequestResponse,
} from '../models/approval.model';
import { ApiResponse } from '@shared/models/api-response.models';
import { VerificationRequiredResponse } from '@shared/models/verification.model';
import { VerificationService } from '@shared/services/verification.service';
import { withSuppressSuccessToast } from '@core/http/api-response.envelope';
import {
  inclusiveDayEndIso,
  inclusiveDayStartIso,
} from '@shared/utils/filter-date-range';

/**
 * Approval Service
 * Handles all HTTP requests related to approval requests
 */
@Injectable({
  providedIn: 'root',
})
export class ApprovalService {
  private readonly baseUrl = `${environment.policyManagementApiUrl}/api/v1/approval-requests`;
  private readonly verificationService = inject(VerificationService);

  constructor(private http: HttpClient) {}

  /**
   * Get list of approval requests
   */
  getApprovalRequests(
    page: number = 1,
    limit: number = 10,
    filters?: ApprovalFilters,
    orderBy: ApprovalOrderBy = 'createdAt',
    order: ApprovalOrderDirection = 'DESC'
  ): Observable<GetApprovalRequestsResponse> {
    const params = this.buildParams(page, limit, filters, orderBy, order);
    return this.http.get<GetApprovalRequestsResponse>(this.baseUrl, { params });
  }

  /**
   * Get my approval requests (where user is an approver)
   */
  getMyApprovalRequests(
    page: number = 1,
    limit: number = 10,
    filters?: ApprovalFilters,
    orderBy: ApprovalOrderBy = 'createdAt',
    order: ApprovalOrderDirection = 'DESC'
  ): Observable<GetApprovalRequestsResponse> {
    const params = this.buildParams(page, limit, filters, orderBy, order);
    return this.http.get<GetApprovalRequestsResponse>(`${this.baseUrl}/me`, {
      params,
    });
  }

  /**
   * Get the approval requests the current user *created* — the
   * `'myRequests'` audience on the approval list page. Same wire shape
   * as the approval-as-approver endpoints; the `/created-by-me` segment
   * is the only thing that differs.
   *
   * `requesterIds` is dropped from the params on this audience because
   * the rows already belong to the current user; `action` is forwarded
   * so the filter sheet can chip-pick activity types.
   */
  getMyRequests(
    page: number = 1,
    limit: number = 10,
    filters?: ApprovalFilters,
    orderBy: ApprovalOrderBy = 'createdAt',
    order: ApprovalOrderDirection = 'DESC'
  ): Observable<GetMyRequestsResponse> {
    const params = this.buildParams(page, limit, filters, orderBy, order);
    return this.http.get<GetMyRequestsResponse>(
      `${this.baseUrl}/created-by-me`,
      { params }
    );
  }

  /**
   * POST /:id/cancel — withdraw a pending request the current user
   * owns. Reason is optional; consumers can omit it for now in case the
   * backend evolves to require one.
   */
  cancelApprovalRequest(
    id: string,
    reason?: string
  ): Observable<CancelApprovalRequestResponse> {
    const body: CancelApprovalRequestBody = reason ? { reason } : {};
    return this.http.post<CancelApprovalRequestResponse>(
      `${this.baseUrl}/${id}/cancel`,
      body
    );
  }

  /**
   * Get unique requesters for filtering
   */
  getRequesters(): Observable<GetRequestersResponse> {
    return this.http.get<GetRequestersResponse>(`${this.baseUrl}/requesters`);
  }

  /**
   * POST /:id/approve (initial call — no actionId).
   *
   * Returns a discriminated union:
   *   - `verification_required` — backend responded 202 with verification metadata,
   *     caller must surface an OTP modal, verify the code via `verifyCode`,
   *     then retry the approval via `confirmApprove(id, actionId, reason?)`.
   *   - `success` — backend approved synchronously (200) and the caller can
   *     navigate / refresh as usual.
   *
   * The success-toast interceptor is suppressed here because:
   *   - On 202, the body's `message` is the literal "verification_required"
   *     code (not user-facing copy).
   *   - On 200, the calling screen already provides its own success UX
   *     (navigation back to the list); we let `confirmApprove` own the
   *     localized success toast for the verification-gated path.
   */
  approveRequest(
    id: string,
    reason?: string
  ): Observable<ApproveRequestResult> {
    const body = reason ? { reason } : {};
    return this.http
      .post<ApiResponse<unknown> | VerificationRequiredResponse>(
        `${this.baseUrl}/${id}/approve`,
        body,
        {
          observe: 'response',
          context: withSuppressSuccessToast(),
        }
      )
      .pipe(
        switchMap(res => {
          if (res.status === 202) {
            const data = this.verificationService.normalizeVerificationRequired(
              res.body as VerificationRequiredResponse
            );
            return of<ApproveRequestResult>({
              type: 'verification_required',
              data,
            });
          }
          return of<ApproveRequestResult>({
            type: 'success',
            response: (res.body as ApiResponse<unknown>) ?? {
              statusCode: res.status,
              message: '',
              data: null,
            },
          });
        })
      );
  }

  /**
   * POST /:id/approve (confirmation call with `actionId`).
   *
   * Issued after the user-submitted code is verified by verification-management.
   * The success toast is intentionally NOT suppressed — this is the response
   * whose localized `message` should reach the user.
   */
  confirmApprove(
    id: string,
    actionId: string,
    reason?: string
  ): Observable<ApiResponse<unknown>> {
    const body: ConfirmApproveRequestBody = reason
      ? { actionId, reason }
      : { actionId };
    return this.http.post<ApiResponse<unknown>>(
      `${this.baseUrl}/${id}/approve`,
      body
    );
  }

  /**
   * POST /:id/reject (initial call — no actionId).
   *
   * Mirrors `approveRequest`: the backend may respond 202 with verification
   * metadata when an OTP / TOTP factor is required before the rejection can
   * be recorded. The caller is expected to surface an OTP modal, verify the
   * code via `verifyCode`, then retry via `confirmReject(id, actionId, reason)`.
   *
   * The success-toast interceptor is suppressed for the same reasons as
   * `approveRequest`:
   *   - On 202, the body's `message` is the literal "verification_required"
   *     code (not user-facing copy).
   *   - On 200 (no verification needed), the calling screen already provides
   *     navigation feedback, and the verification-gated path lets
   *     `confirmReject` own the localized success toast.
   */
  rejectRequest(id: string, reason: string): Observable<RejectRequestResult> {
    return this.http
      .post<ApiResponse<unknown> | VerificationRequiredResponse>(
        `${this.baseUrl}/${id}/reject`,
        { reason },
        {
          observe: 'response',
          context: withSuppressSuccessToast(),
        }
      )
      .pipe(
        switchMap(res => {
          if (res.status === 202) {
            const data = this.verificationService.normalizeVerificationRequired(
              res.body as VerificationRequiredResponse
            );
            return of<RejectRequestResult>({
              type: 'verification_required',
              data,
            });
          }
          return of<RejectRequestResult>({
            type: 'success',
            response: (res.body as ApiResponse<unknown>) ?? {
              statusCode: res.status,
              message: '',
              data: null,
            },
          });
        })
      );
  }

  /**
   * POST /:id/reject (confirmation call with `actionId`).
   *
   * Issued after the user-submitted code is verified by verification-management.
   * The success toast is intentionally NOT suppressed — this is the response
   * whose localized `message` should reach the user.
   */
  confirmReject(
    id: string,
    actionId: string,
    reason: string
  ): Observable<ApiResponse<unknown>> {
    const body: ConfirmRejectRequestBody = { actionId, reason };
    return this.http.post<ApiResponse<unknown>>(
      `${this.baseUrl}/${id}/reject`,
      body
    );
  }

  /**
   * Build HTTP params from pagination and filters
   */
  private buildParams(
    page: number,
    limit: number,
    filters: ApprovalFilters | undefined,
    orderBy: ApprovalOrderBy,
    order: ApprovalOrderDirection
  ): HttpParams {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString())
      .set('orderBy', orderBy)
      .set('order', order);

    if (filters) {
      if (filters.requesterIds && filters.requesterIds.length > 0) {
        params = params.set('requesterIds', filters.requesterIds.join(','));
      }
      if (filters.domain) {
        params = params.set('domain', filters.domain);
      }
      if (filters.status) {
        params = params.set('status', filters.status);
      }
      // Only the `'myRequests'` audience forwards `action`; the param
      // is ignored by the backend on the other endpoints.
      if (filters.action) {
        params = params.set('action', filters.action);
      }
      // Both bounds are whole days, inclusive: from the start of the first
      // to the end of the last (policy-management filters createdAt <=
      // createdTo), the same bridge the transactions list uses (F-116).
      const createdFrom = inclusiveDayStartIso(filters.createdFrom);
      if (createdFrom) {
        params = params.set('createdFrom', createdFrom);
      }
      const createdTo = inclusiveDayEndIso(filters.createdTo);
      if (createdTo) {
        params = params.set('createdTo', createdTo);
      }
    }

    return params;
  }
}
